import React, { useCallback } from 'react';
import TextField, { type TextFieldProps } from '@mui/material/TextField';
import type { ControllerRenderProps, FieldPath, FieldValues } from 'react-hook-form';

function assignRef<T>(ref: React.Ref<T> | undefined, value: T | null): void {
  if (ref == null) return;
  if (typeof ref === 'function') (ref as React.RefCallback<T>)(value);
  else (ref as React.MutableRefObject<T | null>).current = value;
}

const numberInputWheelHandlerByEl = new WeakMap<HTMLInputElement, (e: WheelEvent) => void>();

function useNumberInputRefWithoutWheel(externalRef: React.Ref<HTMLInputElement> | undefined) {
  return useCallback(
    (el: HTMLInputElement | null) => {
      assignRef(externalRef, el);
      if (!el) return;
      const prev = numberInputWheelHandlerByEl.get(el);
      if (prev) el.removeEventListener('wheel', prev);
      const onWheel = (e: WheelEvent) => e.preventDefault();
      numberInputWheelHandlerByEl.set(el, onWheel);
      el.addEventListener('wheel', onWheel, { passive: false });
    },
    [externalRef]
  );
}

export function NumberTextFieldNoWheelRHF<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  field,
  ...textFieldProps
}: Omit<TextFieldProps, 'inputRef'> & {
  field: ControllerRenderProps<TFieldValues, TName>;
}) {
  const inputRef = useNumberInputRefWithoutWheel(field.ref);
  const { ref: _rhfRef, ...fieldRest } = field;
  return <TextField {...fieldRest} {...textFieldProps} inputRef={inputRef} />;
}
